import { WebsocketSendActions } from "@bbj-types/websockets/subscribe";
import { WebsocketStateCode, WsReadyState } from "../types";
import { AWebsocketState } from "./AWebsocketState";
import { DisconnectedState } from "./DisconnectedState";
import { IdleState } from "./IdleState";
import { InactiveState } from "./InactiveState";

export const HEARTBEAT_PAYLOAD = { action: WebsocketSendActions.MESSAGE, message: "p" };
export const NOT_TRY_TO_RECONNECT_ERROR_CODE = new Set([WebsocketStateCode.POLICY_VIOLATION]);

export class ConnectedState extends AWebsocketState {
  private heartbeatId: number | null = null;
  private inactivityTimerId: number | null = null;
  private offVisibility?: () => void;

  private readonly handleVisibilityChange = (): void => {
    if (this.client.getVisibility().isHidden()) {
      this.stopHeartbeat();
    } else {
      this.touchActivity();
      this.sendHeartbeat();
      this.startHeartbeat();
    }
  };

  override enter(): void {
    this.client.onOpenHandler?.();

    this.offVisibility = this.client.getVisibility().onChange(this.handleVisibilityChange);

    this.resetInactivityTimer();

    if (!this.client.getVisibility().isHidden()) {
      this.sendHeartbeat();
      this.startHeartbeat();
    }
    this.client.flushOutbox();
  }

  override exit(): void {
    this.client.onCloseHandler?.();
    this.stopHeartbeat();
    this.clearInactivityTimer();
    this.offVisibility?.();
    this.offVisibility = undefined;
  }

  override onMessage(ev: MessageEvent): void {
    this.client.onMessageHandler?.(ev.data);
    this.touchActivity();
    this.client.emitMessage(ev);
  }

  override onClose(ev: CloseEvent): void {
    console.log("ConnectedState: socket closed", ev.code, ev.reason);

    if (this.client.isShutdownRequested()) {
      this.client.clearShutdownRequest();
      this.client.setState(new IdleState(this.client));
      return;
    }
    if (ev.code === WebsocketStateCode.INACTIVITY_TIMEOUT) {
      this.client.setState(new InactiveState(this.client));
      return;
    }
    if (NOT_TRY_TO_RECONNECT_ERROR_CODE.has(ev.code)) {
      this.client.setState(new IdleState(this.client));
      return;
    }
    this.client.setState(new DisconnectedState(this.client));
  }

  override disconnect(): void {
    this.client.requestShutdown();
    this.client.getWebsocket()?.close(WebsocketStateCode.NORMAL_CLOSURE, "client disconnect");
  }

  override send(message: unknown): void {
    const ws = this.client.getWebsocket();
    if (ws && ws.readyState === WsReadyState.OPEN) {
      this.client.sendJson(message);
    }
  }

  private startHeartbeat(): void {
    if (this.heartbeatId !== null) {
      return;
    }
    this.heartbeatId = this.client.getClock().setInterval(() => {
      this.sendHeartbeat();
    }, this.client.getHeartbeatMs());
  }
  private stopHeartbeat(): void {
    if (this.heartbeatId !== null) {
      this.client.getClock().clearInterval(this.heartbeatId);
      this.heartbeatId = null;
    }
  }
  private sendHeartbeat(): void {
    const ws = this.client.getWebsocket();
    if (ws && ws.readyState === WsReadyState.OPEN) {
      this.client.sendJson(HEARTBEAT_PAYLOAD);
    }
  }
  private resetInactivityTimer(): void {
    this.clearInactivityTimer();
    this.inactivityTimerId = this.client.getClock().setTimeout(() => {
      console.log("ConnectedState: inactivity timeout - forcing disconnect");
      this.client.getWebsocket()?.close(WebsocketStateCode.INACTIVITY_TIMEOUT, "inactivity timeout");
    }, this.client.getInactivityMs());
  }
  private clearInactivityTimer(): void {
    if (this.inactivityTimerId !== null) {
      this.client.getClock().clearTimeout(this.inactivityTimerId);
      this.inactivityTimerId = null;
    }
  }
  private touchActivity(): void {
    this.resetInactivityTimer();
  }
}
